<template>
    <v-row
        v-if="travelersCount"
        row
        wrap
        :style="setListStyle"
        class="mb-6 pa-2"
        id="travelersInfo">
        <v-col cols="12" class="subheading">
            {{ formattedLabel(
                metadata[`description${this.upperLocale}`],
                required
            ) }}
        </v-col>

        <v-col
            v-for="(i, index) in travelersCount"
            cols="12"
            :sm="setCardWidth(6)"
            :md="setCardWidth(4)"
            :lg="setCardWidth(3)"
            :key="index"
        >
            <v-card color="#DFDFDF">
                <v-card-title class="body-1">
                    {{ $t('form.traveler.cardTitle') }} # {{ (index+1) }}
                </v-card-title>

                <v-divider />

                <v-list dense color="#DFDFDF">
                    <v-list-item>
                        <v-list-item-content>
                            <v-text-field
                                v-model="travelersInfo[index].firstname"
                                :label="(required ? '* ' : '') + $t('form.traveler.firstName')"
                                class="ucFirst body-1"
                                dense
                                :rules="[
                                    !required || requiredField(),
                                    v => !v || getFormRule('name')(v)
                                ]"
                                name="traveler_first_name"
                                @input="debouncedInput"
                            />
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-content>
                            <v-text-field
                                v-model="travelersInfo[index].lastname"
                                :label="(required ? '* ' : '') + $t('form.traveler.lastName')"
                                class="ucFirst body-1 mr-1"
                                dense
                                :rules="[
                                    !required || requiredField(),
                                    v => !v || getFormRule('name')(v)
                                ]"
                                name="traveler_last_name"
                                @input="debouncedInput"
                            />
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-content>
                            <v-text-field
                                v-model="travelersInfo[index].age"
                                :label="(required ? '* ' : '') + $t('form.age')"
                                class="ucFirst body-1 mr-1"
                                dense
                                type="number"
                                min="0"
                                max="120"
                                :rules="[
                                    !required || requiredField(),
                                    v => !v || getFormRule('name')(v),
                                    v => validateAge(v) ? true : ''
                                ]"
                                name="traveler_age"
                                @input="debouncedInput"
                            />
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-content class="align-end">
                            <v-select
                                v-model="travelersInfo[index].status"
                                :label="(required ? '* ' : '') + $t('form.status')"
                                class="status-dropdown body-1 "
                                dense
                                :items="statusText"
                                :rules="[
                                    !required || requiredField(),
                                    v => !v || getFormRule('name')(v)
                                ]"
                                name="traveler_status"
                                @input="debouncedInput"
                            />
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-card>
        </v-col>
    </v-row>
</template>

<script>
import { inputHelpers } from '@/mixins/inputHelpers';
import { cloneDeep, debounce, find, size } from 'lodash';
import { mapGetters } from 'vuex';
import { formRules } from '@/mixins/form-rules';
import { MetadataEnum, ServiceSettings } from '@/enums';

export default {
    name: 'travel-travelers-info',
    mixins: [formRules, inputHelpers],
    props: {
        metadata: { type: Object, required: true },
        outline: { type: Boolean, required: false, default: false },
        required: { type: Boolean, required: false, default: false },
        value: { type: [String, Object], required: false, default: '' },
        withIcon: { type: Boolean, required: false, default: false },
        placeholder: { type: String, required: false, default: '' },
    },
    data() {
        return {
            debouncedInput: undefined,
            statusText: [
                this.$i18n.t('form.child'),
                this.$i18n.t('form.adult'),
            ],
        };
    },
    created() {
        this.debouncedInput = debounce(this.emitValue, 500);
    },
    computed: {
        ...mapGetters({
            upperLocale: 'upperLocale',
            currentEstSettings: 'services/getCurrentEstSettings',
            getCopySelectedServiceMetadataValuesOverrideApplied: 'services/getCopySelectedServiceMetadataValuesOverrideApplied',
        }),

        appendIcon() {
            return this.withIcon ? 'far fa-user' : '';
        },
        travelersCount() {
            const meta = this.getCopySelectedServiceMetadataValuesOverrideApplied;
            let count = parseInt(meta[MetadataEnum.TRAVEL_TRAVELER_COUNT].value);
            let minimumTraveler = find(this.currentEstSettings, ['setting', ServiceSettings.TRAVEL_MIN_TRAVELER_COUNT]);
            minimumTraveler = minimumTraveler.value;
            let maximumTraveler = find(this.currentEstSettings, ['setting', ServiceSettings.TRAVEL_MAX_TRAVELER_COUNT]);
            maximumTraveler = maximumTraveler.value;

            if (isNaN(count)) {
                if (minimumTraveler === maximumTraveler) {
                    return parseInt(minimumTraveler);
                }
                return null;
            }
            if (count > maximumTraveler || count < minimumTraveler) {
                return null;
            }
            return count;
        },
        travelersInfo() {
            return this.buildObject();
        },

        setListStyle() {
            return 'border: 2px solid ' + this.$vuetify.theme.themes.light.primary + ';';
        },
    },
    methods: {
        emitValue() {
            this.$emit('update:value', JSON.stringify(this.travelersInfo));
        },

        buildObject() {
            let info = {};
            let start = 0;
            let i = 0;
            if (this.value) {
                info = cloneDeep(JSON.parse(this.value));
                start = size(info);
            }

            // add empty element to end of object
            for(i = start; i < this.travelersCount; i++) {
                info[i] = {
                    firstname: '',
                    lastname: '',
                    age: '',
                    status: '',
                };
            }

            // remove elements from object when travelersCount decrease
            if (size(info) > this.travelersCount) {
                for (i = size(info); i > this.travelersCount; i--) {
                    delete info[(i) - 1];
                }
            }
            return info;
        },

        validateAge(count) {
            return parseInt(count) >= 0 && parseInt(count) <= 120;
        },

        setCardWidth(defaultGridWidth) {
            if (this.travelersCount === 1) {
                return 12;
            }
            if (this.travelersCount === 2) {
                return 6;
            }
            if (this.travelersCount === 3 && this.$vuetify.breakpoint.mdAndUp) {
                return 4;
            }
            return defaultGridWidth;
        },
    },
};
</script>

<style lang="scss" scoped>

</style>
